import {FC, useContext, useEffect, useState} from "react";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import {FaPlusCircle, FaSave, FaTrash} from "react-icons/fa";
import UnitsList from "../../components/admin/units/UnitsList";
import {AppContext} from "../../context/AppContext";
import {AnimatePresence} from "framer-motion";
import Modal from "../../components/ui/Modal";
import Input from "../../components/ui/Input";
import {IGraphUser, IUnit} from "../../types/ApiTypes";
import Autocomplete from "../../components/ui/Autocomplete";
import {SubmitHandler, useForm} from "react-hook-form";
import {toast} from "react-hot-toast";
import axios from "axios";
import useApi from "../../hooks/useApi";
import handleToastError from "../../helpers/handleToastError";

interface InputFields {
    name: string
}

const Units: FC = () => {
    const {state: {units, user},dispatch} = useContext(AppContext);
    const {register, handleSubmit, setValue} = useForm<InputFields>();
    const {createUnit,deleteUnit,editUnit} = useApi()

    const [isOpen, setIsOpen] = useState(false);
    const [unitOptions, setUnitOptions] = useState<{label: string, value: IUnit}[]>([]);
    const [userOptions, setUserOptions] = useState<{label: string, value: IGraphUser}[]>([]);

    const [selectedOwner, setSelectedOwner] = useState<{label: string, value: IGraphUser}|undefined>();
    const [selectedUnit, setSelectedUnit] = useState<{label:string, value: IUnit}|undefined>();

    const [unitToBeEdited, setUnitToBeEdited] = useState<IUnit|undefined>();
    const [editMode, setEditMode] = useState(false);
    const [confirmationModal, setConfirmationModal] = useState(false);

    useEffect(() => {
        if(units) {
            setUnitOptions(units.main.concat(units.secondary).map(x => ({label:x.name, value: x})));
        }
        if(user) {
            setUserOptions(user.map(x => ({label:x.displayName, value:x})));
        }
    },[units,user])

    const onUnitSelected = (type: {label:string, value:IUnit}) => {
        setSelectedUnit(type);
    }

    const onUserSelected = (type: {label:string, value: IGraphUser}) => {
        setSelectedOwner(type)
    }

    const onSubmit: SubmitHandler<InputFields> = async (e) => {
        if(!selectedOwner) return;

        if(editMode) {
            const unit = await toast.promise(editUnit(unitToBeEdited!.id, {
                name: e.name,
                owner: selectedOwner.value.id,
                parent_unit: selectedUnit ? selectedUnit.value.id : undefined
            }),{
                loading: 'Der Formular wird abgeschickt',
                success: <span className='text-sm'>Der Bereich {e.name} wurde bearbeitet.</span>,
                error: (err) => handleToastError(err, `Der Bereich ${e.name} konnte nicht bearbeitet werden`)
            });
            dispatch({type: 'EDIT_UNIT', payload: unit});
            setIsOpen(false);
            setValue('name','');
            setSelectedUnit(undefined);
            setSelectedOwner(undefined);
            setEditMode(false);
            setUnitToBeEdited(undefined);
        } else {
            const unit = await toast.promise(createUnit({
                name: e.name,
                owner: selectedOwner.value.id,
                parent_unit: selectedUnit ? selectedUnit.value.id : undefined
            }),{
                loading: 'Der Formular wird abgeschickt',
                success: <span className='text-sm'>Der Bereich {e.name} wurde erstellt.</span>,
                error: (err) => handleToastError(err, `Der Bereich ${e.name} konnte nicht erstellt werden`)
            });
            dispatch({type: 'APPEND_UNIT', payload: unit});
            setIsOpen(false);
            setValue('name','');
            setSelectedUnit(undefined);
            setSelectedOwner(undefined);
        }
    }

    const onEditSelected = (id:number) => {
        let unit = units.main.find(x => x.id === id);
        if(!unit) unit = units.secondary.find(x => x.id === id);
        if(!unit) return;
        setUnitToBeEdited(unit);
        setValue('name', unit.name);
        if(unit.parent_unit) {
            setSelectedUnit({
                label: unit.parent_unit.name,
                value: unit.parent_unit
            });
        }
        const user = userOptions.find(x => x.value.id === unit!.owner);
        if(user) setSelectedOwner({
            label: user.label,
            value: user.value
        });
        setEditMode(true);
        setIsOpen(true);
    }

    const onDeleteSelected = (id:number) => {
        let unit = units.main.find(x => x.id === id);
        if(!unit) unit = units.secondary.find(x => x.id === id);
        if(!unit) return;
        setUnitToBeEdited(unit);
        setConfirmationModal(true);
    }

    const onModalClose = () => {
        setValue('name','');
        setSelectedOwner(undefined);
        setSelectedUnit(undefined);
        setUnitToBeEdited(undefined);
        setIsOpen(false);
        setEditMode(false);
    }

    const deleteSelectedUnit = async () => {
        alert('Deletion triggered')
        if(!unitToBeEdited) return;
        await toast.promise(deleteUnit(unitToBeEdited.id),{
            loading: 'Die Bearbeitung läuft',
            success: (unit) => {
                dispatch({type: 'REMOVE_UNIT', payload: unit});
                return <span className='text-sm'>Der Bereich {unitToBeEdited.name} wurde gelöscht</span>
            },
            error: (err) => handleToastError(err, "Der Bereich konnte nicht bearbeitet werden")
        });
        setUnitToBeEdited(undefined);
        setConfirmationModal(false);
    }

    return (
        <>
            <Card>
                <UnitsList units={units} editSelected={onEditSelected} deleteSelected={onDeleteSelected}/>
                <div className='flex justify-between items-center mt-3'>
                    <ul className='flex gap-2'>
                        <li>
                            <Button type='button'>{'<<'}</Button>
                        </li>
                        <li>
                            <Button type='button'>{'<'}</Button>
                        </li>
                        <li>
                            <Button type='button'>{'>'}</Button>
                        </li>
                        <li>
                            <Button type='button'>{'>>'}</Button>
                        </li>
                    </ul>
                    <Button type='button' onClick={() => setIsOpen(true)}><FaPlusCircle className='mr-2'/> Neues Bereich erstellen</Button>
                </div>
            </Card>
            <AnimatePresence>
                {isOpen && (
                    <Modal isOpen={isOpen} onClose={onModalClose} title={`${editMode ? 'Bereich bearbeiten' : "Neues Bereich erstellen"}`}>
                        <form className='space-y-2' onSubmit={handleSubmit(onSubmit)}>
                            <div>
                                <label htmlFor='name' className='font-semibold'>Bereichsname</label>
                                <Input id='name' label='Bereichsname' {...register('name')}/>
                            </div>
                            <div>
                                <label htmlFor='parent' className='font-semibold'>Parent unit</label>
                                <Autocomplete<IUnit> options={unitOptions} defaultValue={selectedUnit} onChange={onUnitSelected}/>
                            </div>
                            <div>
                                <label htmlFor="owner" className='font-semibold'>Besitzer</label>
                                <Autocomplete<IGraphUser> options={userOptions} defaultValue={selectedOwner} onChange={onUserSelected}/>
                            </div>
                            {!editMode && <Button type='submit'><FaPlusCircle className='mr-2'/>Erstellen</Button>}
                            {editMode && <Button type='submit' className='bg-success-darker hover:shadow-success-darker/20'><FaSave className='mr-2'/>Speichern</Button>}
                        </form>
                    </Modal>
                )}
                {confirmationModal && (
                    <Modal isOpen={confirmationModal} onClose={() => {setConfirmationModal(false); setUnitToBeEdited(undefined)}} title='Bereich löschen'>
                        Bist du sicher, dass du den Bereich "{unitToBeEdited?.name}" löschen möchtest?
                        <div className='flex justify-end gap-4'>
                            <Button onClick={deleteSelectedUnit} type='button' className='bg-error-brighter hover:shadow-error-brighter/20'><FaTrash className='mr-2'/>Ja</Button>
                            <Button onClick={() => setConfirmationModal(false)} type='button'>Nein</Button>
                        </div>
                    </Modal>
                )}
            </AnimatePresence>
        </>
    );
};

export default Units;